import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { equipmentApi } from '@entities/equipment';

import { moveAllInventoryInCache } from '../lib/moveAllInventoryInCache';
import { moveFilteredInventoryInCache } from '../lib/moveFilteredInventoryInCache';

import type { MoveWarehouseListItem } from './types';
import type { EquipmentDataQueryCache } from '@entities/equipment';

interface UseEditWarehouseParams {
    warehouseId: number;
    itemsCount: number;
    inventoryGuids: string[];
    hasFilterValue: boolean;
}

export const useEditWarehouse = ({
    warehouseId,
    itemsCount,
    inventoryGuids,
    hasFilterValue,
}: UseEditWarehouseParams) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isMoveMode, setIsMoveMode] = useState(false);
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
    const equipmentDataQueryKey = equipmentApi.getEquipmentDataQueryOptions().queryKey;

    const deleteWarehouseMutation = useMutation({
        mutationFn: equipmentApi.deleteWarehouse,
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getEquipmentDataQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getWarehouseListQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getAvailableProjectsQueryOptions().queryKey,
                }),
            ]);
        },
    });

    const invalidateMoveTotalInfoQueries = async ({
        fromWarehouseId,
        toWarehouseId,
    }: {
        fromWarehouseId: number;
        toWarehouseId: number;
    }) => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
            }),
            queryClient.invalidateQueries({
                queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({
                    warehouseId: fromWarehouseId,
                }).queryKey,
            }),
            queryClient.invalidateQueries({
                queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({
                    warehouseId: toWarehouseId,
                }).queryKey,
            }),
        ]);
    };

    const moveAllInventoryMutation = useMutation({
        mutationFn: equipmentApi.moveAllInventory,
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: equipmentDataQueryKey });

            const previousEquipmentData =
                queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);

            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                moveAllInventoryInCache({ previous, ...variables })
            );

            return { previousEquipmentData };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousEquipmentData) {
                queryClient.setQueryData(equipmentDataQueryKey, context.previousEquipmentData);
            }
        },
        onSuccess: (_data, variables) => {
            window.setTimeout(() => {
                void invalidateMoveTotalInfoQueries(variables);
            }, 1000);
        },
    });

    const moveFilteredInventoryMutation = useMutation({
        mutationFn: equipmentApi.moveFilteredInventory,
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: equipmentDataQueryKey });

            const previousEquipmentData =
                queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);

            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                moveFilteredInventoryInCache({
                    previous,
                    fromWarehouseId: warehouseId,
                    ...variables,
                })
            );

            return { previousEquipmentData };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousEquipmentData) {
                queryClient.setQueryData(equipmentDataQueryKey, context.previousEquipmentData);
            }
        },
        onSuccess: (_data, variables) => {
            window.setTimeout(() => {
                void invalidateMoveTotalInfoQueries({
                    fromWarehouseId: warehouseId,
                    toWarehouseId: variables.toWarehouseId,
                });
            }, 1000);
        },
    });

    const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            setIsMoveMode(false);
        }
    };

    const deleteWarehouse = async () => {
        if (itemsCount > 0) {
            setIsDeleteAlertOpen(true);

            return;
        }

        await deleteWarehouseMutation.mutateAsync({ warehouseId });
    };

    const moveInventory = (warehouse: MoveWarehouseListItem, closeDropdown: () => void) => {
        if (hasFilterValue) {
            moveFilteredInventoryMutation.mutate({
                inventoryGuids,
                toWarehouseId: warehouse.id,
            });
        } else {
            moveAllInventoryMutation.mutate({
                fromWarehouseId: warehouseId,
                toWarehouseId: warehouse.id,
            });
        }

        closeDropdown();
    };

    return {
        t,
        isMoveMode,
        isDeleteAlertOpen,
        handleOpenChange,
        openMoveMode: () => setIsMoveMode(true),
        closeDeleteAlert: () => setIsDeleteAlertOpen(false),
        deleteWarehouse,
        moveInventory,
    };
};
